import { useState, useEffect, useRef, useCallback } from 'react';
import { CSVLink } from 'react-csv';
import { canEditByRole, RolesEnum } from '$Externals/VerifyRole';
import { VideoEventService } from '$State/VideoEventFreezerService';
import { VideoEventFilterServiceMultiselect } from '$State/VideoEventFilterFreezerServiceMultiselect';
import { IntegrationPartnerDataService } from '$State/IntegrationPartnerDataFreezerService';
import { isEncompass } from '$Utilities/integrationPlatform';
import { splitTreatmentNames, useGetSplitTreatment } from '$Utilities/split-io-sdk';
import { VideoEventReportResponse, VideoEventResponse } from '$Generated/api';
import { CsvData, getCsvTitle, getExportHeaders, mapEventToCsvRow, VideoEventForExport } from './videoEventExportUtils';

export const useVideoEventExport = () => {
    const [canExport, setCanExport] = useState(false);
    const [videoEventExportData, setVideoEventExportData] = useState<CsvData[]>([]);
    const [csvTitle, setCsvTitle] = useState('Video Events');
    const [companyLevelTreatmentOn, setCompanyLevelTreatmentOn] = useState(false);
    const [isConfirmDialogOpen, setConfirmDialogOpen] = useState(false);

    const csvLinkRef = useRef<CSVLink & HTMLAnchorElement & { link: HTMLAnchorElement }>(null);

    const { getSplitTreatment } = useGetSplitTreatment();

    const isMetric = IntegrationPartnerDataService.getUserIsMetric();
    const showCompanyLevel = isEncompass && companyLevelTreatmentOn;
    const csvHeaders = getExportHeaders(isEncompass, isMetric, showCompanyLevel);

    useEffect(() => {
        setCanExport(canEditByRole([RolesEnum.videoEventView, RolesEnum.videoEventEdit, RolesEnum.encompassVideo]));
    }, []);

    useEffect(() => {
        getSplitTreatment(splitTreatmentNames.videoEventCompanyLevels)
            .then(setCompanyLevelTreatmentOn)
            .catch(() => setCompanyLevelTreatmentOn(false));
    }, []);

    useEffect(() => {
        if (videoEventExportData.length) {
            csvLinkRef.current?.link?.click();
            setVideoEventExportData([]);
        }
    }, [videoEventExportData]);

    const showConfirmDialog = useCallback(
        (show: boolean) => {
            if (!canExport) return;
            setConfirmDialogOpen(show);
        },
        [canExport],
    );

    const downloadCsvBulk = useCallback(async () => {
        if (!canExport) return;
        const filter = VideoEventFilterServiceMultiselect.getVideoEventFilter();
        filter.itemsPerPage = undefined;
        showConfirmDialog(false);
        await VideoEventService.getEventExportDataMultiSelect(filter);
        const { videoEventExportResults } = VideoEventService.getState();
        const myData: VideoEventReportResponse[] = videoEventExportResults.data?.data || [];

        setCsvTitle(getCsvTitle(filter));
        setVideoEventExportData(myData.map((val) => mapEventToCsvRow(val as VideoEventForExport, isEncompass, isMetric)));
    }, [canExport, isEncompass, isMetric, showConfirmDialog]);

    const downloadCsvSingle = useCallback(
        async (event: VideoEventResponse) => {
            if (!canExport) return;
            if (event.id !== undefined) {
                await VideoEventService.getVideoEventAndDriverComments(event.id);
            }
            const comments = VideoEventService.getState().videoEventAndDriverCommentsResult.data ?? [];
            setCsvTitle(`Video_Event_${event.displayId || 'export'}`);
            setVideoEventExportData([mapEventToCsvRow({ ...event, comments } as VideoEventForExport, isEncompass, isMetric)]);
            if (event.id !== undefined) {
                await VideoEventService.logExport(event.id);
            }
        },
        [isEncompass, isMetric, canExport],
    );

    return {
        csvLinkRef,
        videoEventExportData,
        csvTitle,
        csvHeaders,
        canExport,
        isConfirmDialogOpen,
        showConfirmDialog,
        downloadCsvBulk,
        downloadCsvSingle,
    };
};
